import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import styles from "./ReservationSection.module.css";
import { db } from "../../firebase";
import { collection, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore";

// ─── Constants ────────────────────────────────────────────────────────────────

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const WEEKEND = ["Friday", "Saturday", "Sunday"];

const ALL_SLOTS = [
    { id: "12-2",    label: "12 PM – 2 PM" },
    { id: "2-4",     label: "2 PM – 4 PM" },
    { id: "7-9",     label: "7 PM – 9 PM" },
    { id: "9-11",    label: "9 PM – 11 PM" },
    { id: "11-1:30", label: "11 PM – 1:30 AM" },
];

const WEEKDAY_SLOTS = ALL_SLOTS;                // All 5 slots Mon–Thu
const WEEKEND_SLOTS = ALL_SLOTS.slice(0, 3);   // Only first 3 slots for Fri–Sun

function getSlotsForDay(dayName) {
    return WEEKEND.includes(dayName) ? WEEKEND_SLOTS : WEEKDAY_SLOTS;
}

function toDateKey(dateStr) {
    return dateStr;
}

function getDayName(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T12:00:00");
    return WEEKDAYS[(d.getDay() + 6) % 7]; // Mon=0
}

// ─── Component ────────────────────────────────────────────────────────────────

const ReservationSection = ({ isPopup = false }) => {
    const [formData, setFormData] = useState({
        fullName: "",
        email: "",
        phone: "",
        date: "",
        guests: "",
        slot: "",
        message: "",
    });

    const [loading, setLoading] = useState(false);

    // Slot config from Firestore
    const [slotConfig, setSlotConfig] = useState(null);
    const [configLoading, setConfigLoading] = useState(true);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const snap = await getDoc(doc(db, "config", "slotSettings"));
                if (snap.exists()) {
                    const data = snap.data();
                    console.log("✅ Slot config loaded:", JSON.stringify(data, null, 2));
                    setSlotConfig(data);
                } else {
                    console.warn("⚠️ No slotSettings doc found — showing all slots open");
                    setSlotConfig(null);
                }
            } catch (e) {
                console.error("Could not load slot config:", e);
            } finally {
                setConfigLoading(false);
            }
        };
        fetchConfig();
    }, []);

    // Derive available slots for the selected date
    const availableSlots = (() => {
        if (!formData.date) return [];
        const dayName = getDayName(formData.date);
        if (!dayName) return [];
        const baseSlots = getSlotsForDay(dayName);

        // No config saved yet — show everything open
        if (!slotConfig || !slotConfig.weeklySlots) return baseSlots;

        const dateKey = toDateKey(formData.date);
        const weekly = slotConfig.weeklySlots[dayName] || {};
        const override = slotConfig.dateOverrides?.[dateKey] || {};

        // Merge: weekly first, override patches on top
        const source = { ...weekly, ...override };

        console.log(`📅 ${dateKey} (${dayName}) source:`, source);

        // FIX: treat missing keys as open (undefined !== false)
        // Previously used source[s.id] === true which excluded any slot
        // not explicitly saved, causing partially-saved configs to hide slots
        return baseSlots.filter((s) => source[s.id] !== false);
    })();

    const isDateFullyBlocked = (() => {
        if (!formData.date || !slotConfig || !slotConfig.weeklySlots) return false;
        const dayName = getDayName(formData.date);
        if (!dayName) return false;
        const baseSlots = getSlotsForDay(dayName);
        const dateKey = toDateKey(formData.date);
        const weekly = slotConfig.weeklySlots[dayName] || {};
        const override = slotConfig.dateOverrides?.[dateKey] || {};
        const source = { ...weekly, ...override };
        // FIX: a slot is only blocked when explicitly set to false, not when merely absent
        return baseSlots.every((s) => source[s.id] === false);
    })();

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Reset slot if date changes
        if (name === "date") {
            setFormData((prev) => ({ ...prev, date: value, slot: "" }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.slot) {
            window.alert("Please select a time slot.");
            return;
        }
        setLoading(true);
        try {
            // 1. Store in Firestore (client SDK, governed by security rules)
            const docRef = await addDoc(collection(db, "reservations"), {
                ...formData,
                status: "pending",
                createdAt: serverTimestamp(),
            });

            // 2. Trigger notification mail — a mail failure shouldn't fail the booking
            try {
                await fetch("/api/reservation", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ ...formData, docId: docRef.id }),
                });
            } catch (mailErr) {
                console.error("Mail notification failed (reservation saved):", mailErr);
            }

            window.alert("Reservation submitted successfully!");
            setFormData({
                fullName: "",
                email: "",
                phone: "",
                date: "",
                guests: "",
                slot: "",
                message: "",
            });
        } catch (error) {
            console.error("Error adding reservation:", error);
            window.alert("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, staggerChildren: 0.1 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <section className={`${styles.wrapper} ${isPopup ? styles.popupMode : ""}`}>
            <motion.div
                className={styles.header}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
            >
                <h1>Make a Reservation</h1>
                <p>
                    For further questions, please call —{" "}
                    <span>8879199982, &ensp; 8879199984</span>
                </p>
                <p className={styles.secLine}>For parties larger than 6, please call us directly.</p>
            </motion.div>

            <motion.div
                className={styles.divider}
                initial={{ width: 0 }}
                whileInView={{ width: "100%" }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease: "easeInOut" }}
            />

            <motion.form
                className={styles.form}
                onSubmit={handleSubmit}
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
            >
                {/* Row 1: Party size + Date */}
                <motion.div className={styles.gridRow2} variants={itemVariants}>
                    <div className={styles.inputWrapper}>
                        <select name="guests" value={formData.guests} onChange={handleChange} required>
                            <option value="" disabled>Party size</option>
                            {[1, 2, 3, 4, 5, 6].map((num) => (
                                <option key={num} value={num}>
                                    {num} {num === 1 ? "Person" : "People"}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputWrapper}>
                        <input
                            type="text"
                            name="date"
                            placeholder="Select Date"
                            onFocus={(e) => (e.target.type = "date")}
                            onBlur={(e) => { if (!e.target.value) e.target.type = "text"; }}
                            value={formData.date}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </motion.div>

                {/* Row 2: Slot selector */}
                <motion.div variants={itemVariants}>
                    {!formData.date ? (
                        <p className={styles.slotHint}>← Select a date to see available time slots</p>
                    ) : configLoading ? (
                        <p className={styles.slotHint}>Loading available slots…</p>
                    ) : isDateFullyBlocked ? (
                        <div className={styles.blockedDay}>
                            <span className={styles.blockedIcon}>✕</span>
                            No reservations available on this date. Please choose another day.
                        </div>
                    ) : availableSlots.length === 0 ? (
                        <div className={styles.blockedDay}>
                            <span className={styles.blockedIcon}>✕</span>
                            No slots available on this date.
                        </div>
                    ) : (
                        <div className={styles.slotGrid}>
                            {availableSlots.map((slot) => (
                                <div
                                    key={slot.id}
                                    className={`${styles.slotCard} ${formData.slot === slot.id ? styles.slotCardSelected : ""}`}
                                    onClick={() => setFormData((prev) => ({ ...prev, slot: slot.id }))}
                                >
                                    {slot.label}
                                </div>
                            ))}
                        </div>
                    )}
                </motion.div>

                {/* Row 3: Name, Email, Phone */}
                <motion.div className={styles.gridRow3} variants={itemVariants}>
                    <div className={styles.inputWrapper}>
                        <input
                            type="text"
                            name="fullName"
                            placeholder="Your Name"
                            value={formData.fullName}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className={styles.inputWrapper}>
                        <input
                            type="email"
                            name="email"
                            placeholder="Email address"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className={styles.inputWrapper}>
                        <input
                            type="tel"
                            name="phone"
                            placeholder="Phone Number"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                        />
                    </div>
                </motion.div>

                {/* Row 4: Special requests */}
                <motion.div className={styles.gridRow1} variants={itemVariants}>
                    <div className={styles.inputWrapper}>
                        <textarea
                            name="message"
                            placeholder="Any special requests? (Optional)"
                            value={formData.message}
                            onChange={handleChange}
                            rows="2"
                            className={styles.textarea}
                        />
                    </div>
                </motion.div>

                <motion.div className={styles.btnContainer} variants={itemVariants}>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="submit"
                        className={styles.submitBtn}
                        disabled={loading}
                    >
                        {loading ? "Please wait…" : "Find a Table"}
                    </motion.button>
                </motion.div>

            </motion.form>
        </section>
    );
};

export default ReservationSection;